import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { BackIcon } from '../../components/AppHeader';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { red, green } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { camelCase } from 'lodash-es';
import { getChronos, setChronoGun, addChronoDaily, addChronoRequest, getShooters, postShooterChrono } from '../../api';
import Snackbar from '../../components/Snackbar';
import { getFlagSrc } from '../../utils/scoring';

// ---------------------------------------------------------------------------
// Main Chrono page - manages state and sub-routing
// ---------------------------------------------------------------------------
export default function Chrono() {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const [chronoData, setChronoData] = useState(null);
  const [shooters, setShooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const stations = chronoData?.stations || [];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [chronoRes, shooterRes] = await Promise.all([
        getChronos(matchId),
        getShooters(matchId),
      ]);
      const cd = chronoRes.data;
      setChronoData(cd && cd.stations ? cd : { matchId, stations: [] });
      setShooters(Array.isArray(shooterRes.data) ? shooterRes.data : []);
    } catch {
      setSnack({ open: true, message: t('common.error'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateChrono = useCallback((data) => {
    if (data && data.stations) setChronoData(data);
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Routes>
        <Route index element={
          <ChronoMain
            matchId={matchId} stations={stations} shooters={shooters}
            updateChrono={updateChrono} loadData={loadData}
            snack={snack} setSnack={setSnack} t={t}
          />
        } />
        <Route path="station/:stationIdx/newdaily" element={
          <ChronoDailyTest
            matchId={matchId} stations={stations}
            updateChrono={updateChrono} t={t} setSnack={setSnack}
          />
        } />
        <Route path="newrequest/:shooterId" element={
          <ChronoNewRequest
            matchId={matchId} stations={stations} shooters={shooters}
            updateChrono={updateChrono} t={t} setSnack={setSnack}
          />
        } />
        <Route path="station/:stationIdx/request/:timestamp" element={
          <ChronoForm
            matchId={matchId} stations={stations} shooters={shooters}
            loadData={loadData} t={t} setSnack={setSnack}
          />
        } />
      </Routes>
      <Snackbar open={snack.open} message={snack.message} severity={snack.severity}
        onClose={() => setSnack({ ...snack, open: false })} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Station overview - tabs, test gun, daily table, requests table
// ---------------------------------------------------------------------------
function ChronoMain({ matchId, stations, shooters, updateChrono, loadData, snack, setSnack, t }) {
  const navigate = useNavigate();
  const [currentStation, setCurrentStation] = useState(0);
  const [gunOpen, setGunOpen] = useState(false);
  const [gunSaving, setGunSaving] = useState(false);
  const [gunForm, setGunForm] = useState({ make: '', serial: '', weight: '' });

  const station = stations[currentStation] || {};
  const gun = station.gun;
  const dailys = station.dailys || [];
  const requests = station.requests || [];

  const switchStation = (idx) => {
    const st = stations[idx] || {};
    setCurrentStation(idx);
    setGunForm({ make: st.gun?.make || '', serial: st.gun?.serial || '', weight: st.gun?.weight || '' });
    setGunOpen(false);
  };

  useEffect(() => {
    if (gun) setGunForm({ make: gun.make || '', serial: gun.serial || '', weight: gun.weight || '' });
  }, [gun]);

  const handleAddStation = async () => {
    // Just add a station locally (server creates on first gun/daily/request save)
    stations.push({});
    updateChrono({ stations: [...stations] });
    switchStation(stations.length - 1);
  };

  const handleSaveGun = async () => {
    setGunSaving(true);
    try {
      const { data } = await setChronoGun(matchId, currentStation, {
        make: gunForm.make,
        serial: gunForm.serial,
        weight: parseFloat(gunForm.weight) || 0,
      });
      updateChrono(data);
      setGunOpen(false);
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.detail || t('common.error'), severity: 'error' });
    } finally {
      setGunSaving(false);
    }
  };

  const basePath = `/${matchId}/manage/chrono`;

  return (
    <Box>
      {/* Station tabs + add button */}
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Tabs
          variant="fullWidth"
          value={currentStation}
          onChange={(_, v) => switchStation(v)}
          sx={{ flex: 1 }}
        >
          {stations.map((_, i) => (
            <Tab key={i} label={i + 1} sx={{ minWidth: 0 }} />
          ))}
        </Tabs>
        <Button variant="contained" onClick={handleAddStation} sx={{ ml: 1, whiteSpace: 'nowrap' }}>
          + {t('chrono.addStation')}
        </Button>
      </Box>

      {stations.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          {t('scoring.noChronoStations')}
        </Typography>
      )}

      {stations.length > 0 && (
        <>
          {/* Test Gun */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>{t('chrono.testGun')}</Typography>
                {gun && !gunOpen && dailys.length === 0 && (
                  <IconButton color="primary" size="small" onClick={() => setGunOpen(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {gunOpen && (
                  <IconButton color="secondary" size="small" onClick={() => setGunOpen(false)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {(gun || gunOpen) ? (
                <Box>
                  <Grid container spacing={2} sx={{ mb: 1 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label={t('chrono.testMakeModel')} fullWidth required
                        disabled={!gunOpen} value={gunForm.make}
                        onChange={(e) => setGunForm({ ...gunForm, make: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label={t('chrono.testSerial')} fullWidth required
                        disabled={!gunOpen} value={gunForm.serial}
                        onChange={(e) => setGunForm({ ...gunForm, serial: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label={t('chrono.testWeight')} fullWidth required
                        disabled={!gunOpen} value={gunForm.weight}
                        onChange={(e) => setGunForm({ ...gunForm, weight: e.target.value })}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>
                  </Grid>
                  {gunOpen && (
                    <>
                      <Typography variant="caption" color="text.secondary">{t('chrono.testWarnings')}</Typography>
                      <Button fullWidth variant="contained" disabled={gunSaving} onClick={handleSaveGun} sx={{ mt: 1 }}>
                        {gunSaving ? <CircularProgress size={20} /> : t('chrono.testSave')}
                      </Button>
                    </>
                  )}
                </Box>
              ) : (
                <Button color="primary" onClick={() => setGunOpen(true)}>
                  + {t('chrono.testGunAdd')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Daily Results */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>{t('chrono.dailyResults')}</Typography>
              {dailys.length > 0 ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ '& td, & th': { border: '1px solid rgba(0,0,0,0.2)' } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('chrono.dailyDate')}</TableCell>
                        <TableCell colSpan={3}>{t('chrono.dailyShotVelocity')}</TableCell>
                        <TableCell>{t('chrono.dailyAvgVelocity')}</TableCell>
                        <TableCell>{t('chrono.dailyAvgPower')}</TableCell>
                        <TableCell>{t('chrono.dailyChange')}</TableCell>
                        <TableCell>CRO</TableCell>
                        <TableCell>RM</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dailys.map((d, idx) => {
                        const avgV = Math.round(((d.velocity1 + d.velocity2 + d.velocity3) / 3) * 100) / 100;
                        const avgP = Math.floor((gun.weight * avgV) / 1000);
                        const baseAvgV = Math.round(((dailys[0].velocity1 + dailys[0].velocity2 + dailys[0].velocity3) / 3) * 100) / 100;
                        const baseP = Math.floor((gun.weight * baseAvgV) / 1000);
                        const changePct = idx === 0 ? null : Math.round((Math.abs(baseP - avgP) / baseP) * 10000) / 100;
                        const isError = changePct !== null && changePct > 5;

                        return (
                          <TableRow key={idx} sx={isError ? { bgcolor: red[100] } : {}}>
                            <TableCell>{new Date(d.date).toLocaleString()}</TableCell>
                            <TableCell>{d.velocity1}</TableCell>
                            <TableCell>{d.velocity2}</TableCell>
                            <TableCell>{d.velocity3}</TableCell>
                            <TableCell>{avgV}</TableCell>
                            <TableCell>{avgP}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {isError && <ErrorIcon sx={{ color: red[500], fontSize: 18 }} />}
                                {changePct === null ? 'N/A' : `${changePct}%`}
                              </Box>
                            </TableCell>
                            <TableCell>{d.cro}</TableCell>
                            <TableCell>{d.rm}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {gun ? '' : t('chrono.dailyNoTestGun')}
                </Typography>
              )}
              {gun && (
                <Button color="primary" sx={{ mt: 1 }}
                  onClick={() => navigate(`${basePath}/station/${currentStation}/newdaily`)}>
                  + {dailys.length === 0 ? t('chrono.dailyAddFirst') : t('chrono.dailyAdd')}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Requests */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>{t('chrono.requests')}</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ '& td, & th': { border: '1px solid rgba(0,0,0,0.2)' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('chrono.requestDate')}</TableCell>
                      <TableCell>{t('chrono.requestShooter')}</TableCell>
                      <TableCell>{t('chrono.requestRo')}</TableCell>
                      <TableCell sx={{ width: 60 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...requests]
                      .sort((a, b) => b.timestamp - a.timestamp)
                      .map((req) => {
                        const shooter = shooters.find((s) => s.id === req.shooterId) || {};
                        const chrono = (shooter.chronos || []).find((c) => c.requestTimestamp === req.timestamp);
                        const parts = (shooter.publicId || '').split('|');
                        const countryCode = parts.length > 1 ? parts[0] : null;
                        const displayId = parts[parts.length - 1];

                        let actionNode;
                        if (!chrono) {
                          actionNode = (
                            <IconButton color="primary"
                              onClick={() => navigate(`${basePath}/station/${currentStation}/request/${req.timestamp}`)}>
                              <EditIcon />
                            </IconButton>
                          );
                        } else if (chrono.prevPowerFactor === chrono.nextPowerFactor) {
                          actionNode = <CheckCircleIcon sx={{ color: green[500] }} />;
                        } else {
                          actionNode = <CancelIcon sx={{ color: red[500] }} />;
                        }

                        return (
                          <TableRow key={req.timestamp}>
                            <TableCell>{new Date(req.timestamp).toLocaleString()}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {countryCode && <img alt={countryCode} src={getFlagSrc({ countryCode, size: 16 })} />}
                                <span>{displayId}</span>
                                <span>{shooter.name}</span>
                              </Box>
                            </TableCell>
                            <TableCell>{req.ro}</TableCell>
                            <TableCell>{actionNode}</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Daily test form - record 3 velocity readings + initials
// ---------------------------------------------------------------------------
function ChronoDailyTest({ matchId, stations, updateChrono, t, setSnack }) {
  const navigate = useNavigate();
  const { stationIdx: stationIdxStr } = useParams();
  const stationIdx = parseInt(stationIdxStr, 10);

  const now = new Date();
  const pad2 = (n) => String(n).padStart(2, '0');
  const nowStr = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}T${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

  const [velocity1, setVelocity1] = useState('');
  const [velocity2, setVelocity2] = useState('');
  const [velocity3, setVelocity3] = useState('');
  const [date, setDate] = useState(nowStr);
  const [cro, setCro] = useState('');
  const [rm, setRm] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSave = async () => {
    const errs = {};
    if (!parseFloat(velocity1)) errs.velocity1 = true;
    if (!parseFloat(velocity2)) errs.velocity2 = true;
    if (!parseFloat(velocity3)) errs.velocity3 = true;
    if (!cro) errs.cro = true;
    if (!rm) errs.rm = true;
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    try {
      const { data } = await addChronoDaily(matchId, stationIdx, {
        date: new Date(date).getTime(),
        velocity1: parseFloat(velocity1),
        velocity2: parseFloat(velocity2),
        velocity3: parseFloat(velocity3),
        cro,
        rm,
      });
      updateChrono(data);
      navigate(`/${matchId}/manage/chrono`);
    } catch {
      setSnack({ open: true, message: t('common.error'), severity: 'error' });
      setSaving(false);
    }
  };

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate(`/${matchId}/manage/chrono`)} sx={{ mb: 2 }}>
        {t('common.back')}
      </Button>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('chrono.stationNum', { num: stationIdx + 1 })} - {t('chrono.dailyAdd')}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            label={t('chrono.dailyDate')} type="datetime-local" fullWidth
            value={date} onChange={(e) => setDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label={`${t('chrono.dailyShotVelocity')} #1`} fullWidth
            error={!!errors.velocity1} value={velocity1}
            onChange={(e) => { setVelocity1(e.target.value); setErrors({ ...errors, velocity1: false }); }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label={`${t('chrono.dailyShotVelocity')} #2`} fullWidth
            error={!!errors.velocity2} value={velocity2}
            onChange={(e) => { setVelocity2(e.target.value); setErrors({ ...errors, velocity2: false }); }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label={`${t('chrono.dailyShotVelocity')} #3`} fullWidth
            error={!!errors.velocity3} value={velocity3}
            onChange={(e) => { setVelocity3(e.target.value); setErrors({ ...errors, velocity3: false }); }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={`${t('chrono.initials')} - CRO`} fullWidth
            error={!!errors.cro} value={cro}
            onChange={(e) => { setCro(e.target.value); setErrors({ ...errors, cro: false }); }}
          />
        </Grid>
        <Grid size={6}>
          <TextField
            label={`${t('chrono.initials')} - RM`} fullWidth
            error={!!errors.rm} value={rm}
            onChange={(e) => { setRm(e.target.value); setErrors({ ...errors, rm: false }); }}
          />
        </Grid>
        <Grid size={12}>
          <Button fullWidth variant="contained" disabled={saving} onClick={handleSave}>
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// New request - send shooter to chrono station
// ---------------------------------------------------------------------------
function ChronoNewRequest({ matchId, stations, shooters, updateChrono, t, setSnack }) {
  const navigate = useNavigate();
  const { shooterId } = useParams();
  const shooter = shooters.find((s) => s.id === shooterId);

  const [stationIdx, setStationIdx] = useState(0);
  const [scheduling, setScheduling] = useState(false);
  const [done, setDone] = useState(false);

  const handleSchedule = async () => {
    setScheduling(true);
    try {
      const { data } = await addChronoRequest(matchId, stationIdx, { shooterId });
      updateChrono(data);
      setDone(true);
    } catch {
      setSnack({ open: true, message: t('common.error'), severity: 'error' });
    } finally {
      setScheduling(false);
    }
  };

  if (!shooter) return <Typography color="error">Shooter not found</Typography>;

  const parts = (shooter.publicId || '').split('|');
  const countryCode = parts.length > 1 ? parts[0] : null;
  const displayId = parts[parts.length - 1];

  if (done) {
    return (
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('chrono.newRequestDoneTitle')}</Typography>
        <Typography sx={{ mb: 1 }}>{t('chrono.newRequestDone1', { shooter: shooter.name })}</Typography>
        <Typography sx={{ mb: 3 }}>{t('chrono.newRequestDone2', { station: stationIdx + 1 })}</Typography>
        <Button fullWidth variant="contained" onClick={() => navigate(`/${matchId}/manage/chrono`)}>
          {t('common.ok')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        {t('common.back')}
      </Button>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('chrono.newRequestTitle')}</Typography>

      <Typography sx={{ mb: 1 }}>{t('chrono.newRequestDesc1')}</Typography>
      <Typography sx={{ mb: 1 }}>{t('chrono.newRequestDesc2')}</Typography>
      <Typography sx={{ mb: 3 }}>{t('chrono.newRequestDesc3')}</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {countryCode && <img alt={countryCode} src={getFlagSrc({ countryCode, size: 20 })} />}
        <Typography variant="body1">#{displayId} <strong>{shooter.name}</strong></Typography>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{t('chrono.newRequestSelectStation')}</Typography>
      <Select value={stationIdx} onChange={(e) => setStationIdx(e.target.value)} sx={{ mb: 3 }}>
        {stations.map((_, i) => (
          <MenuItem key={i} value={i}>{t('chrono.stationNum', { num: i + 1 })}</MenuItem>
        ))}
      </Select>

      <Button fullWidth variant="contained" disabled={scheduling} onClick={handleSchedule}>
        {scheduling ? <CircularProgress size={20} /> : t('chrono.newRequestSchedule')}
      </Button>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Chrono test form - 3-stage testing procedure
// ---------------------------------------------------------------------------
function ChronoForm({ matchId, stations, shooters, loadData, t, setSnack }) {
  const navigate = useNavigate();
  const { stationIdx: stationIdxStr, timestamp: tsStr } = useParams();
  const stationIdx = parseInt(stationIdxStr, 10);
  const requestTs = parseInt(tsStr, 10);

  const station = stations[stationIdx] || {};
  const requests = station.requests || [];
  const request = requests.find((r) => r.timestamp === requestTs);
  const shooter = shooters.find((s) => s.id === request?.shooterId) || {};

  // Persist form in localStorage
  const storageKey = `__chronoform_result`;
  const stored = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch { return {}; }
  }, []);
  const defaults = (stored.shooterId === request?.shooterId && stored.timestamp === requestTs) ? stored : {};

  const [weight1, setWeight1] = useState(defaults.weight1 || '');
  const [weight2, setWeight2] = useState(defaults.weight2 || '');
  const [velocity1, setVelocity1] = useState(defaults.velocity1 || '');
  const [velocity2, setVelocity2] = useState(defaults.velocity2 || '');
  const [velocity3, setVelocity3] = useState(defaults.velocity3 || '');
  const [velocity4, setVelocity4] = useState(defaults.velocity4 || '');
  const [velocity5, setVelocity5] = useState(defaults.velocity5 || '');
  const [velocity6, setVelocity6] = useState(defaults.velocity6 || '');
  const [velocity7, setVelocity7] = useState(defaults.velocity7 || '');
  const [stage, setStage] = useState(defaults.stage || '');
  const [saving, setSaving] = useState(false);

  // Save to localStorage on every change
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify({
      shooterId: request?.shooterId, timestamp: requestTs,
      weight1, weight2, velocity1, velocity2, velocity3, velocity4, velocity5, velocity6, velocity7, stage,
    }));
  }, [request?.shooterId, requestTs, weight1, weight2, velocity1, velocity2, velocity3, velocity4, velocity5, velocity6, velocity7, stage]);

  if (!request) return <Typography color="error">Request not found</Typography>;

  const allFirst = weight1 && velocity1 && velocity2 && velocity3;
  const allSecond = allFirst && velocity4 && velocity5 && velocity6;
  const allThird = allSecond && ((velocity7 && !weight2) || (!velocity7 && weight2));

  const calculated = (() => {
    if (allFirst && !stage) {
      const avgV = (parseFloat(velocity1) + parseFloat(velocity2) + parseFloat(velocity3)) / 3;
      return Math.floor((avgV * parseFloat(weight1)) / 1000);
    }
    if (stage === 2 && allSecond) {
      const vels = [velocity1, velocity2, velocity3, velocity4, velocity5, velocity6].map((v) => parseFloat(v));
      vels.sort((a, b) => b - a);
      const avgV = (vels[0] + vels[1] + vels[2]) / 3;
      return Math.floor((avgV * parseFloat(weight1)) / 1000);
    }
    if (stage === 3 && allThird) {
      const vels = [velocity1, velocity2, velocity3, velocity4, velocity5, velocity6, velocity7 || '0'].map((v) => parseFloat(v));
      vels.sort((a, b) => b - a);
      const avgV = (vels[0] + vels[1] + vels[2]) / 3;
      const w = Math.max(parseFloat(weight1), parseFloat(weight2 || '0'));
      return Math.floor((avgV * w) / 1000);
    }
    return null;
  })();

  const allOk = (() => {
    if (calculated === null || calculated < 125) return false;
    if (shooter['power-factor'] === 'minor') return true;
    if (['standard', 'classic'].includes(shooter.division) && calculated >= 170) return true;
    if (shooter.division === 'open' && calculated >= 160) return true;
    return false;
  })();

  const nextPowerFactor = (() => {
    if (allOk) return shooter['power-factor'];
    if (calculated !== null && calculated < 125) return 'invalid';
    return 'minor';
  })();

  const handleNextStage = () => {
    if (!stage) setStage(2);
    else if (stage === 2) setStage(3);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = {
        requestTimestamp: request.timestamp,
        prevPowerFactor: shooter['power-factor'] || 'minor',
        nextPowerFactor,
        weight1: parseFloat(weight1) || 0,
        weight2: parseFloat(weight2) || 0,
        velocity1: parseFloat(velocity1) || 0,
        velocity2: parseFloat(velocity2) || 0,
        velocity3: parseFloat(velocity3) || 0,
      };
      if (velocity4) result.velocity4 = parseFloat(velocity4);
      if (velocity5) result.velocity5 = parseFloat(velocity5);
      if (velocity6) result.velocity6 = parseFloat(velocity6);
      if (velocity7) result.velocity7 = parseFloat(velocity7);

      await postShooterChrono(matchId, request.shooterId, result);
      localStorage.removeItem(storageKey);
      await loadData();
      navigate(`/${matchId}/manage/chrono`);
    } catch (e) {
      setSnack({ open: true, message: e.response?.data?.detail || t('common.error'), severity: 'error' });
      setSaving(false);
    }
  };

  const pf = shooter['power-factor'];
  const shooterInfo = [
    shooter.division ? t(`divisions.${camelCase(shooter.division)}`, shooter.division) : null,
    pf ? t(`registration.${camelCase(pf)}`, pf) : null,
  ].filter(Boolean).join(' | ');

  return (
    <Box>
      <Button startIcon={<BackIcon />} onClick={() => navigate(`/${matchId}/manage/chrono`)} sx={{ mb: 2 }}>
        {t('common.back')}
      </Button>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('chrono.formTitle')}</Typography>

      {/* Shooter info */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label={t('scoring.shooterInfo')} value={shooter.name || ''} fullWidth
            slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label={t('manage.division')} value={shooter.division ? t(`divisions.${camelCase(shooter.division)}`, shooter.division) : ''} fullWidth
            slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label={t('manage.powerFactor')} value={pf ? t(`registration.${camelCase(pf)}`, pf) : ''} fullWidth
            slotProps={{ input: { readOnly: true }, inputLabel: { shrink: true } }} />
        </Grid>
      </Grid>

      {/* Stage 1: First test (4 bullets) */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('chrono.formFirstTest')}
        {!!stage && (
          <Button size="small" sx={{ ml: 1, textTransform: 'none' }} onClick={() => setStage('')}>
            {t('common.edit')}
          </Button>
        )}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField label={t('chrono.formBulletWeight1')} fullWidth disabled={!!stage}
            value={weight1} onChange={(e) => setWeight1(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label={t('chrono.formBulletVelocity1')} fullWidth disabled={!!stage}
            value={velocity1} onChange={(e) => setVelocity1(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label={t('chrono.formBulletVelocity2')} fullWidth disabled={!!stage}
            value={velocity2} onChange={(e) => setVelocity2(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label={t('chrono.formBulletVelocity3')} fullWidth disabled={!!stage}
            value={velocity3} onChange={(e) => setVelocity3(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} />
        </Grid>
      </Grid>

      {/* Stage 2: Second test (3 more bullets) */}
      {(stage === 2 || stage === 3) && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('chrono.formSecondTest')}
            {stage !== 2 && (
              <Button size="small" sx={{ ml: 1, textTransform: 'none' }} onClick={() => setStage(2)}>
                {t('common.edit')}
              </Button>
            )}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label={t('chrono.formBulletVelocity4')} fullWidth disabled={stage !== 2}
                value={velocity4} onChange={(e) => setVelocity4(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label={t('chrono.formBulletVelocity5')} fullWidth disabled={stage !== 2}
                value={velocity5} onChange={(e) => setVelocity5(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label={t('chrono.formBulletVelocity6')} fullWidth disabled={stage !== 2}
                value={velocity6} onChange={(e) => setVelocity6(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
          </Grid>
        </>
      )}

      {/* Stage 3: Last chance (1 more bullet) */}
      {stage === 3 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>{t('chrono.formLastChanceTitle')}</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label={t('chrono.formBulletVelocity7')} fullWidth
                value={velocity7} onChange={(e) => setVelocity7(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField label={t('chrono.formBulletWeight2')} fullWidth
                value={weight2} onChange={(e) => setWeight2(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} />
            </Grid>
          </Grid>
        </>
      )}

      {/* Result display + actions */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">
          {calculated === null
            ? (stage === 3 && allSecond && !allThird
              ? t('chrono.formResultLastChance')
              : t('chrono.formResultMissing'))
            : t('chrono.formResult', { calculated })}
        </Typography>

        {/* Stage 1 failed -> offer stage 2 */}
        {!stage && allFirst && !allOk && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'center' }}>
              {t('chrono.formNotOk')}
            </Typography>
            <Button fullWidth variant="contained" color="secondary" onClick={handleNextStage} sx={{ mt: 1 }}>
              {t('chrono.formAnotherThree')}
            </Button>
          </Box>
        )}

        {/* Stage 2 failed -> offer stage 3 */}
        {stage === 2 && allSecond && !allOk && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'center' }}>
              {t('chrono.formNotOk')}
            </Typography>
            <Button fullWidth variant="contained" color="secondary" onClick={handleNextStage} sx={{ mt: 1 }}>
              {t('chrono.formLastChance')}
            </Button>
          </Box>
        )}

        {/* Stage 3 failed -> save with result */}
        {stage === 3 && allThird && !allOk && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'center' }}>
              {calculated >= 125 ? t('chrono.formToMinor') : t('chrono.formStopScoring')}
            </Typography>
            <Button fullWidth variant="contained" color="secondary" onClick={handleSave} disabled={saving} sx={{ mt: 1 }}>
              {saving ? <CircularProgress size={20} /> : t('common.save')}
            </Button>
          </Box>
        )}

        {/* Passed -> save */}
        {allOk && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500, textAlign: 'center', color: green[700] }}>
              {t('chrono.formAllOk')}
            </Typography>
            <Button fullWidth variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 1 }}>
              {saving ? <CircularProgress size={20} /> : t('common.save')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
